import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/api/core/auth/auth.service';
import { CoreService } from 'src/app/api/core/core/core.service';
import { AnalyticsService } from 'src/app/api/utils/analytics/analytics.service';

import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import has from 'lodash/has';
import size from 'lodash/size';
import { BffService } from 'src/app/api/utils/comms/bff/bff.service';
import { GeneratedDocketComponentType, IGeneratedDocket } from '../repository/generated-docket-repository.service';

export interface IFormattedDocket {
  receipt: string[];
  split_dockets: {
    [group_id: number]: string[];
  };
}

export interface IDocketConfigItem {
  sequence: number;
  label: string;
  visibility: boolean;
  visibility_editable: boolean;
  type: DocketItemType;
}

export enum DocketItemType {
  ETA = 'eta',
  ORDER_DATE = 'order_date',
  LOCATION = 'location',
  ORDER_ID = 'order_id',
  PAYMENT = 'payment',
  NAME = 'name',
  EMAIL = 'email',
  MOBILE = 'mobile',
  FULFILMENT = 'fulfilment',
  UNPAID = 'unpaid',
  FOOD_ITEM = 'food_item',
  TOTAL = 'total',
}

@Injectable({
  providedIn: 'root',
})
export class DocketLayoutConfigService {
  private _configComponentWithSpaceBefore = [
    DocketItemType.UNPAID,
    DocketItemType.FULFILMENT,
    DocketItemType.FOOD_ITEM,
    DocketItemType.TOTAL,
  ];

  /** Flagger for when the service is still trying to retrieve the docket config */
  private _retrievingConfig: boolean = false;

  /** Flagger for when the service is still trying to save the docket config */
  private _saveConfig: boolean = false;

  /** The docket layout config */
  private _docket_layout_items: Array<IDocketConfigItem> = [];

  constructor(
    public bffService: BffService,
    public auth: AuthService,
    public core: CoreService,
    public analytics: AnalyticsService,
  ) {}

  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.core.Active_server.bff.token}`,
    });
  }

  /**
   * Request the docket layout configurate to build the UI
   * @returns Promise with the payload from the HTTP request or Fail object
   */
  public async getDocketLayoutConfiguration() {
    try {
      // show loading indicator
      this._retrievingConfig = true;
      const payload: any = await this.bffService.getBFFDocketConfiguration();

      this._retrievingConfig = false;

      if (payload && payload.success) {
        this._updateDocketConfig(payload.data.config);
        return this._docket_layout_items;
      } else {
        this.analytics.trackEvent(
          'Docket Config',
          `Failed GET: ${this.auth.Active_bump_screen.id}`,
          this.auth.Tracking_key,
        );

        return [];
      }
    } catch (e: any) {
      this._retrievingConfig = false;

      this.analytics.trackEvent(
        'Docket Config',
        `Error UPDATE: ${e.message} : ${this.auth.Active_bump_screen.id}`,
        this.auth.Tracking_key,
      );

      return [];
    }
  }

  /**
   * Saves this.docket_items configuration to DOCKET SERVICE
   *
   * @param IDocketConfig[] The new docket config to be saved
   * @returns Boolean status of success update
   */
  async saveDocketConfig(config: IDocketConfigItem[]) {
    try {
      // show loading indicator
      this._saveConfig = true;

      const payload: any = await this.bffService.updateBFFDocketConfig({
        id: this.auth.Active_bump_screen.id,
        config: config,
      });

      this._saveConfig = false;

      if (payload && payload.success) {
        this._updateDocketConfig(config);
        return payload.success;
      } else {
        this.analytics.trackEvent(
          'Docket Config',
          `Failed UPDATE: ${this.auth.Active_bump_screen.id}`,
          this.auth.Tracking_key,
        );

        return false;
      }
    } catch (e) {
      this._saveConfig = false;
      this.analytics.trackEvent(
        'Docket Config',
        `Error UPDATE: ${this.auth.Active_bump_screen.id}`,
        this.auth.Tracking_key,
      );

      return false;
    }
  }

  private _sortConfigItems() {
    this._docket_layout_items = this._docket_layout_items.sort((a, b) => {
      return a.sequence - b.sequence;
    });
  }

  /**
   * Formats all the the receipt and split_dockets of the generated docket to
   * print ready state. This will result to an array of string for each docket component.
   *
   * @param docketData - The generated docket component
   * @returns Formatted docket object where receipt and split_docket is going to be an array of string
   */
  format(docketData: IGeneratedDocket): IFormattedDocket {
    console.log('Docket data to format', docketData);

    let retVal: IFormattedDocket = {
      receipt: [],
      split_dockets: {},
    };

    if (has(docketData, 'receipt') && size(docketData)) {
      retVal.receipt = this._formatDocket(docketData.receipt);
    }

    each(docketData.split_dockets, (splitDocket: GeneratedDocketComponentType, printerGroupId: number) => {
      retVal.split_dockets[printerGroupId] = this._formatDocket(splitDocket);
    });

    return retVal;
  }

  /**
   * Applies the saved docket layout config to a generated docket component
   * @param data Docket component such as receipt or a split_docket property
   * @returns Docket string array
   */
  private _formatDocket(data: GeneratedDocketComponentType): string[] {
    let retVal = [];

    if (size(this._docket_layout_items)) {
      const visibleItems: IDocketConfigItem[] = this._docket_layout_items.filter((docketItem: IDocketConfigItem) => {
        return docketItem.visibility;
      });

      each(visibleItems, (item: IDocketConfigItem, index: number) => {
        if (has(data, item.type) && size(data[item.type])) {
          if (this._configComponentWithSpaceBefore.indexOf(item.type) > -1 && index > 0) {
            retVal = [...retVal, '', ...data[item.type]];
          } else {
            retVal = [...retVal, ...data[item.type]];
          }
        }
      });
    } else {
      // print default if layout config is not available
      each(data, component => {
        retVal = [...retVal, ...component];
      });
    }

    return retVal;
  }

  /**
   * Updates the save docket config
   * @param config The new docket config to save
   */
  private _updateDocketConfig(config: IDocketConfigItem[]) {
    this._docket_layout_items = cloneDeep(config);
    this._sortConfigItems();
  }

  public reset() {
    this._retrievingConfig = false;
    this._saveConfig = false;
    this._docket_layout_items = [];
  }

  public get RetrievingConfig(): boolean {
    return this._retrievingConfig;
  }

  public get SavingConfig(): boolean {
    return this._saveConfig;
  }

  public get Docket_layout_config(): IDocketConfigItem[] {
    return this._docket_layout_items;
  }
}
